import { useState } from 'react';
import {
  NJSpinner,
  NJInlineMessage,
} from '@engie-group/fluid-design-system-react';
import PrototypeStudioLayout from '@shared/PrototypeStudioLayout';
import { useBuilding, useChangeLog } from './hooks';
import { BuildingIdPanel } from './BuildingIdPanel';
import { BuildingSummaryPanel } from './BuildingSummaryPanel';
import { ChangeLogTable } from './ChangeLogTable';
import { ConsumptionsTab } from './ConsumptionsTab';
import { CharacteristicsTab } from './CharacteristicsTab';
import type { ActiveTab } from './types';

// ─── Meta ─────────────────────────────────────────────────────────────────────

export const meta = {
  title: 'Journal des modifications — Bâtiment',
  description:
    'Onglet "Journal des modifications" dans la fiche bâtiment UrbanMap, intégré dans la page d\'accueil avec sidebar UM, panel bâtiment droit et panel étendu.',
  tags: ['building', 'detail', 'list'],
  author: 'urbanmap-team',
  status: 'draft' as const,
};

// ─── Main component ───────────────────────────────────────────────────────────

const BUILDING_ID = 'BFR15720257';

const TABS: { id: ActiveTab; label: string }[] = [
  { id: 'consumptions', label: 'Consommations' },
  { id: 'characteristics', label: 'Caractéristiques détaillées' },
  { id: 'change-log', label: 'Journal des modifications' },
];

export default function BuildingChangeLog() {
  const [activeTab, setActiveTab] = useState<ActiveTab>('change-log');

  const { data: building, isLoading: buildingLoading, isError: buildingError } = useBuilding(BUILDING_ID);
  const { data: changeLog, isLoading: logLoading, isError: logError } = useChangeLog(BUILDING_ID);

  const isLoading = buildingLoading || logLoading;
  const isError = buildingError || logError;

  return (
    <PrototypeStudioLayout title="UrbanMap — Journal des modifications">
      <div style={{ display: 'flex', width: '100%', height: 'calc(100vh - 56px)', overflow: 'hidden' }}>
      {/* ─── Extended panel: IDs + Tabs + Journal ─── */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden', position: 'relative' }}>
        {isLoading && (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <NJSpinner scale="lg" isLoading />
          </div>
        )}
        {isError && !isLoading && (
          <div style={{ flex: 1, padding: 24 }}>
            <NJInlineMessage variant="error" title="Erreur de chargement">
              Impossible de récupérer les données du bâtiment.
            </NJInlineMessage>
          </div>
        )}
        {!isLoading && !isError && building && (
          <>
            <BuildingIdPanel building={building} />
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              {/* Tab header */}
              <div style={{ display: 'flex', borderBottom: '2px solid #e2e8f0', padding: '0 16px', flexShrink: 0 }}>
                {TABS.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    style={{
                      padding: '12px 16px', border: 'none', cursor: 'pointer', fontSize: 13,
                      fontWeight: activeTab === tab.id ? 600 : 400, background: 'transparent',
                      color: activeTab === tab.id ? '#009de9' : '#64748b',
                      borderBottom: activeTab === tab.id ? '2px solid #009de9' : '2px solid transparent',
                      marginBottom: -2, whiteSpace: 'nowrap',
                    }}
                  >
                    {tab.id === 'change-log' && changeLog ? `${tab.label} (${changeLog.length})` : tab.label}
                  </button>
                ))}
              </div>
              {/* Tab content */}
              <div style={{ flex: 1, overflow: 'hidden', position: 'relative', display: 'flex', flexDirection: 'column' }}>
                {activeTab === 'consumptions' && <ConsumptionsTab building={building} />}
                {activeTab === 'characteristics' && <CharacteristicsTab building={building} />}
                {activeTab === 'change-log' && changeLog && (
                  <ChangeLogTable entries={changeLog} />
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* ─── Building summary panel (right) ─── */}
      {building && <BuildingSummaryPanel building={building} />}
      </div>
    </PrototypeStudioLayout>
  );
}
